import { FC } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "@/hooks/useStore";
import {
	AlertDialogHeader,
	AlertDialogFooter,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface ErrorModalProps {
	title: string;
	description?: string;
	allowClose?: boolean;
	showRefresh?: boolean;
	extraContent?: React.ReactNode;
	error?: unknown;
}

export const ErrorModal: FC<ErrorModalProps> = observer(
	({ title, description, allowClose = false, showRefresh = true, extraContent, error }) => {
		const {
			uiStore: { modalStore },
		} = useStore();

		const onRefresh = () => {
			window.location.reload();
		};

		return (
			<>
				<AlertDialogHeader>
					<div className="flex items-start gap-3">
						<AlertTriangle className="text-destructive mt-1 shrink-0" />
						<div>
							<AlertDialogTitle className="text-destructive text-lg">{title}</AlertDialogTitle>
							{description && (
								<AlertDialogDescription className="mt-1 text-sm text-muted-foreground">
									{description}
								</AlertDialogDescription>
							)}
						</div>
					</div>
				</AlertDialogHeader>

				{error && (
					<div className="mt-2 text-xs bg-muted p-2 rounded">
						<pre className="whitespace-pre-wrap break-words">{String(error)}</pre>
					</div>
				)}

				{extraContent && <div className="mt-4 text-sm">{extraContent}</div>}

				<AlertDialogFooter className="mt-6">
					{allowClose && <AlertDialogCancel onClick={() => modalStore.close()}>Close</AlertDialogCancel>}
					{showRefresh && (
						<Button onClick={onRefresh} variant="destructive">
							Refresh App
						</Button>
					)}
				</AlertDialogFooter>
			</>
		);
	},
);
